/**
 * Screen ↔ lon/lat for the Scale overlay.
 * Uses the 2D map camera (lon/lat + zoom) and the map container size.
 */
import { mercatorZoomForCameraHeight } from '@/engines/maplibre/overlayZoom';

const ORIGIN_SHIFT = 20037508.342789244;
const TILE_SIZE = 256;

export interface ScaleViewCamera {
  readonly lonLat?: readonly [number, number] | [number, number];
  readonly zoom?: number;
  readonly height?: number;
}

export interface ScaleProjector {
  clientToLonLat(clientX: number, clientY: number): [number, number] | null;
  lonLatToClient(lon: number, lat: number): { x: number; y: number } | null;
}

export function lonLatToMercator(lon: number, lat: number): [number, number] {
  const x = (lon / 180) * ORIGIN_SHIFT;
  const latRad = (lat * Math.PI) / 180;
  const y = (Math.log(Math.tan(Math.PI / 4 + latRad / 2)) / Math.PI) * ORIGIN_SHIFT;
  return [x, y];
}

export function mercatorToLonLat(x: number, y: number): [number, number] {
  const lon = (x / ORIGIN_SHIFT) * 180;
  const latRad = Math.atan(Math.exp((y / ORIGIN_SHIFT) * Math.PI));
  const lat = ((2 * latRad - Math.PI / 2) * 180) / Math.PI;
  return [lon, lat];
}

export function lonLatToWorldPx(lon: number, lat: number, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const x = ((lon + 180) / 360) * scale;
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sine = Math.sin((clamped * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sine) / (1 - sine)) / (4 * Math.PI)) * scale;
  return { x, y };
}

export function worldPxToLonLat(x: number, y: number, zoom: number): [number, number] {
  const scale = TILE_SIZE * 2 ** zoom;
  const lon = (x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * y) / scale;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(n));
  return [lon, lat];
}

export function resolveScaleZoom(camera: ScaleViewCamera | null | undefined, viewportHeightPx: number): number | null {
  if (typeof camera?.zoom === 'number' && Number.isFinite(camera.zoom)) return camera.zoom;
  const lat = camera?.lonLat?.[1];
  if (typeof camera?.height === 'number' && typeof lat === 'number') {
    return mercatorZoomForCameraHeight({
      heightMeters: camera.height,
      latDeg: lat,
      viewportHeightPx,
    });
  }
  return null;
}

export function createScaleProjector(
  container: HTMLElement | null,
  camera?: ScaleViewCamera | null,
): ScaleProjector {
  return {
    clientToLonLat(clientX, clientY) {
      const view = readView(container, camera);
      if (!view) return null;
      const world = lonLatToWorldPx(view.lon, view.lat, view.zoom);
      const x = world.x + (clientX - view.rect.left - view.rect.width / 2);
      const y = world.y + (clientY - view.rect.top - view.rect.height / 2);
      return worldPxToLonLat(x, y, view.zoom);
    },
    lonLatToClient(lon, lat) {
      const view = readView(container, camera);
      if (!view) return null;
      const center = lonLatToWorldPx(view.lon, view.lat, view.zoom);
      const point = lonLatToWorldPx(lon, lat, view.zoom);
      return {
        x: view.rect.left + view.rect.width / 2 + (point.x - center.x),
        y: view.rect.top + view.rect.height / 2 + (point.y - center.y),
      };
    },
  };
}

function readView(container: HTMLElement | null, camera?: ScaleViewCamera | null) {
  const rect = container?.getBoundingClientRect();
  const lon = camera?.lonLat?.[0];
  const lat = camera?.lonLat?.[1];
  if (!rect || rect.width <= 0 || rect.height <= 0) return null;
  if (typeof lon !== 'number' || typeof lat !== 'number') return null;
  const zoom = resolveScaleZoom(camera, rect.height);
  if (zoom == null) return null;
  return { lon, lat, zoom, rect };
}

export function resolveMapRoot(container?: HTMLElement | null): HTMLElement | null {
  if (container) return container;
  if (typeof document === 'undefined') return null;
  const canvases = [...document.querySelectorAll('canvas')];
  if (canvases.length === 0) return null;
  return canvases.reduce((best, node) =>
    node.width * node.height > best.width * best.height ? node : best,
  ).parentElement;
}
